import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { orderWindowForRun } from "@/utils/smsShared";

/**
 * How many invitable past customers are waiting further back than yesterday
 * (the day today's batch covers) and have never been sent an invite — the
 * number the backlog sweep still has to work through. Counts v_customer_invite_status with the same
 * filters invite_backlog queries. Read-only.
 */
export const useBacklogCount = () => {
  const getSupabaseClient = useSupabaseClient();
  const { data: settings } = useMarketingSettings();
  const timezone = settings?.timezone;

  return useQuery<number>({
    queryKey: ["sms-backlog-count", timezone],
    enabled: !!settings,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { start: windowStart } = orderWindowForRun(
        new Date(),
        timezone ?? "America/Los_Angeles",
      );
      const { count, error } = await sb
        .from("v_customer_invite_status")
        .select("customer_phone", { count: "exact", head: true })
        .eq("is_invitable", true)
        .eq("history_invite_count", 0)
        .lt("last_order_at", windowStart);
      if (error) throw error;
      return count ?? 0;
    },
    staleTime: 60 * 1000,
  });
};

export interface BacklogSweepResult {
  ok: boolean;
  queued?: number;
  campaign_id?: string;
  error?: string;
}

/** Runs one backlog batch via sms-actions' invite_backlog action, then
 *  refreshes the batch/forecast/count queries so the UI reflects the send. */
export const useBacklogSweep = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation<BacklogSweepResult, Error, void>({
    mutationFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke("sms-actions", {
        body: { action: "invite_backlog" },
      });
      if (error) throw new Error(error.message ?? "Backlog sweep failed");
      const result = data as BacklogSweepResult;
      if (!result?.ok) throw new Error(result?.error ?? "Backlog sweep failed");
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-backlog-count"] });
      queryClient.invalidateQueries({ queryKey: ["sms-invite-batches"] });
      queryClient.invalidateQueries({ queryKey: ["sms-invite-forecast"] });
    },
  });
};
